import { useCallback, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { Popover, PopoverButton, Indicator } from '@crazyegginc/hatch';

import { ConditionsPopover } from '../ConditionsPopover';
import { AUDIENCE_METADATA } from '../../metadata';
import { addCondition } from '../../utils';
import { ConditionTooltip } from './ConditionTooltip';

export function Criteria({ index, innerIndex, mode, onConditionSelected, onUpsellClick }) {
  const { values, setFieldValue } = useFormikContext();
  const tooltipRowSnapshotRef = useRef(null);

  const isFirstRow = innerIndex === 0;
  const condition = values.conditions[index];
  const criteria = condition.criteria;
  const metadata = AUDIENCE_METADATA[criteria];
  const { label } = metadata;
  const showTooltip = condition.tooltip === 'criteria';
  const tooltipRowSnapshot = getTooltipRowSnapshot(condition, innerIndex);

  function onSelectCriteria(newCriteria) {
    addCondition(index, newCriteria, values, setFieldValue);
    onConditionSelected?.(newCriteria);
  }

  const onTooltipDismiss = useCallback(() => {
    setFieldValue(`conditions[${index}].tooltip`, false, false);
  }, [index, setFieldValue]);

  useLayoutEffect(() => {
    if (!isFirstRow || !showTooltip) {
      tooltipRowSnapshotRef.current = tooltipRowSnapshot;
      return;
    }

    if (tooltipRowSnapshotRef.current == null) {
      tooltipRowSnapshotRef.current = tooltipRowSnapshot;
      return;
    }

    if (tooltipRowSnapshotRef.current !== tooltipRowSnapshot) {
      onTooltipDismiss();
    }

    tooltipRowSnapshotRef.current = tooltipRowSnapshot;
  }, [isFirstRow, onTooltipDismiss, showTooltip, tooltipRowSnapshot]);

  if (!isFirstRow)
    return (
      <div className="w-[160px] shrink-0 h-[35px] flex items-center justify-end text-body-5 font-semibold leading-none">
        and
      </div>
    );

  return (
    <Popover>
      <ConditionTooltip criteria={metadata} type="criteria" show={showTooltip} onDismiss={onTooltipDismiss}>
        <div className="relative w-[160px] shrink-0">
          <PopoverButton
            className={classNames(
              'flex h-[35px] w-full grow items-center justify-between rounded px-2.5 cursor-pointer',
              'border border-mystic-500 bg-white focus:border-dodger-blue-500 focus:outline-none',
            )}
          >
            <span className={classNames('text-body-1 leading-tight truncate')}>{label}</span>
            <Indicator type="dropdown" className="ml-2.5" />
          </PopoverButton>
          <ConditionsPopover
            anchor="top start"
            mode={mode}
            onClick={onSelectCriteria}
            onUpsellClick={onUpsellClick ? (UpsellModal) => onUpsellClick(values, UpsellModal) : undefined}
          />
        </div>
      </ConditionTooltip>
    </Popover>
  );
}

function getTooltipRowSnapshot(condition, rowIndex) {
  const row = condition.rows[rowIndex];

  return JSON.stringify({
    criteria: condition.criteria,
    comparison: row?.comparison,
    values: row?.values,
    rowsLength: condition.rows.length,
  });
}
